import { createSelector } from 'reselect';

import { formatErrorMessages } from '../../../utils/globalHelpers';
import { VIEW_NAMESPACE_DICTIONARY } from './constants';
import { initialState } from './reducer';
import makeSelectMobile from '../../Mobile/selectors';
import { FORGOT_USERNAME_PASSWORD_ALLOWED_VIEWS } from '../Shared/constants';

/**
 * Direct selector to the Forgot Username state domain
 */
const selectForgotUsernameDomain = (state) => state.forgotUsername || initialState;

/**
 * Direct selector to Forgot Username props
 */
const selectForgotUsernameProps = (state, props) => props;

const getForgotUsernameConfirmationPageProps = () => createSelector(
  makeSelectForgotUsername('error'),
  getForgotUsernameStatusByPageView(),
  makeSelectForgotUsername('recoveredUsername'),
  (errors, { namespace: statusNamespace, status }, recoveredUsername) => {
    let errorOrSuccess = '';
    let message = '';
    const resetStep = 1;

    if (status === 'succeeded') {
      errorOrSuccess = `${statusNamespace}Success`;
    } else if (status === 'failed') {
      const error = Object.entries(errors).find(([, value]) => !!value);
      if (error) {
        const [errorKey, errorValue] = error;
        errorOrSuccess = errorKey;
        message = errorValue;
      } else {
        errorOrSuccess = `${statusNamespace}Error`;
      }
    }
    return {
      errorOrSuccess,
      hasError: status === 'failed',
      message,
      recoveredUsername,
      resetStep,
    };
  },
);

const getForgotUsernameError = (prop) => createSelector(
  makeSelectForgotUsername('error'),
  (error) => error[prop],
);

const getForgotUsernameForm = () => createSelector(
  makeSelectForgotUsername('form'),
  getForgotUsernameViewNamespace(),
  (form, viewNamespace) => form[viewNamespace] || {},
);

const getForgotUsernameFormErrors = () => createSelector(
  makeSelectForgotUsername('formErrors'),
  getForgotUsernameViewNamespace(),
  (formErrors, viewNamespace) => formErrors[viewNamespace] || {},
);

const getForgotUsernameNotifications = () => createSelector(
  getForgotUsernameError('bannerError'),
  getForgotUsernameError('serviceError'),
  getForgotUsernameFormErrors('forgotUsername'),
  getForgotUsernameFormErrors('verifyUsername'),
  (
    bannerError,
    serviceError,
    forgotUsernameErrors,
    verifyUsernameErrors,
  ) => {
    const nonFormErrors = [bannerError, serviceError];
    const notifications = nonFormErrors.filter((error) => !!error);
    return notifications.concat(formatErrorMessages({
      ...forgotUsernameErrors,
      ...verifyUsernameErrors,
    }));
  },
);

const getForgotUsernamePageView = () => createSelector(
  selectForgotUsernameProps,
  makeSelectMobile('isMobile'),
  (props, isMobile) => {
    const propName = isMobile ? 'route' : 'match';
    const { params = {} } = props[propName] || {};
    const view = isMobile ? params.view : params[0];
    if (view) {
      return FORGOT_USERNAME_PASSWORD_ALLOWED_VIEWS.includes(view) ? view : '';
    }
    return 'init';
  },
);

const getForgotUsernameQueryString = () => createSelector(
  selectForgotUsernameProps,
  ({ route }) => {
    const { params = {} } = route;
    return encodeURIComponent(params.q);
  },
);

const getForgotUsernameStatusByPageView = () => createSelector(
  makeSelectForgotUsername('status'),
  getForgotUsernamePageView(),
  (status, view) => {
    if (view === 'init') {
      return {
        namespace: 'forgotUsernameInitiate',
        status: status.forgotUsernameInitiate,
      };
    }
    if (view === 'verify') {
      return {
        namespace: 'forgotUsernameVerify',
        status: status.forgotUsernameVerify,
      };
    }
    return {};
  },
);

const getForgotUsernameStep = () => createSelector(
  makeSelectForgotUsername('step'),
  getForgotUsernamePageView(),
  (step, view) => step[view] || 1,
);

const getForgotUsernameViewNamespace = () => createSelector(
  getForgotUsernameStep(),
  getForgotUsernamePageView(),
  (step, view) => view ? VIEW_NAMESPACE_DICTIONARY[view][step] : '',
);

/**
 * Default selector used by Forgot Username
 */
const makeSelectForgotUsername = (prop) => createSelector(
  selectForgotUsernameDomain,
  (state) => state[prop],
);

export default makeSelectForgotUsername;
export {
  getForgotUsernameConfirmationPageProps,
  getForgotUsernameError,
  getForgotUsernameForm,
  getForgotUsernameFormErrors,
  getForgotUsernameNotifications,
  getForgotUsernamePageView,
  getForgotUsernameQueryString,
  getForgotUsernameStep,
  getForgotUsernameViewNamespace,
};
